import { useCallback, useRef, useState } from "react";

const AUDIO_INITIAL_DELAY = 0.3;

const useAudioPlayback = () => {
  const refAudioContext = useRef<AudioContext | null>(null);
  const refNextTime = useRef(0);
  const refSources = useRef<AudioBufferSourceNode[]>([]);
  const refPendingDecodes = useRef(0);
  const refDoneReceived = useRef(false);
  const refAudio = useRef<HTMLAudioElement | null>(null);

  const [statePlaybackStatus, setStatePlaybackStatus] = useState<"idle" | "playing">("idle");
  const setStateIsPlaying = useCallback(
    (isPlaying: boolean) => {
      setStatePlaybackStatus(isPlaying ? "playing" : "idle");
    },
    [setStatePlaybackStatus],
  );

  const stopAudio = useCallback(() => {
    if (refAudio.current) {
      refAudio.current.pause();
      refAudio.current.src = "";
      refAudio.current = null;
    }
    refSources.current.forEach((source) => {
      try {
        source.stop(0);
      } catch {
        /* ignore stop error on already-stopped source */
      }
    });
    refSources.current = [];
    refNextTime.current = 0;
    refPendingDecodes.current = 0;
    refDoneReceived.current = false;
    if (refAudioContext.current) {
      void refAudioContext.current.close();
      refAudioContext.current = null;
    }
    setStateIsPlaying(false);
  }, [setStateIsPlaying]);

  const playFromUrl = useCallback(
    (url: string) => {
      if (!refAudio.current) refAudio.current = new Audio();
      refAudio.current.pause();
      refAudio.current.src = url;
      refAudio.current.onended = () => setStateIsPlaying(false);
      void refAudio.current.play().catch(() => {
        setStateIsPlaying(false);
      });
    },
    [setStateIsPlaying],
  );

  const attachOnEndedToLastSource = useCallback(() => {
    const last = refSources.current[refSources.current.length - 1];
    if (last) last.onended = () => setStateIsPlaying(false);
  }, [setStateIsPlaying]);

  const scheduleChunk = useCallback(
    (audioBytes: number[]) => {
      if (!refAudioContext.current) refAudioContext.current = new AudioContext();
      const ctx = refAudioContext.current;
      const uint8 = new Uint8Array(audioBytes);
      refPendingDecodes.current += 1;
      void ctx.decodeAudioData(
        uint8.buffer,
        (buffer) => {
          refPendingDecodes.current -= 1;
          if (refAudioContext.current !== ctx) return;
          const source = ctx.createBufferSource();
          source.buffer = buffer;
          source.connect(ctx.destination);
          if (refNextTime.current === 0) refNextTime.current = ctx.currentTime + AUDIO_INITIAL_DELAY;
          source.start(refNextTime.current);
          refNextTime.current += buffer.duration;
          refSources.current.push(source);
          if (refDoneReceived.current && refPendingDecodes.current === 0) attachOnEndedToLastSource();
        },
        () => {
          refPendingDecodes.current -= 1;
          if (refDoneReceived.current && refPendingDecodes.current === 0) attachOnEndedToLastSource();
        },
      );
    },
    [attachOnEndedToLastSource],
  );

  return {
    stopAudio,
    scheduleChunk,
    attachOnEndedToLastSource,
    setStateIsPlaying,
    refDoneReceived,
    refPendingDecodes,
    isPlaying: statePlaybackStatus === "playing",
    playFromUrl,
  };
};

export default useAudioPlayback;
